import { ASSETS } from './assets-data';

const privacyData = {
  en: {
    seo: {
      title: 'Privacy Policy | Tropical Marine Construction',
      description: 'Learn how Tropical Marine Construction collects, uses, and protects your personal information when you visit our website, request an estimate, or contact our team.',
      keywords: 'Tropical Marine Construction privacy policy, Cape Coral marine contractor privacy, SWFL dock builder privacy, SMS consent'
    },

    heroSection: {
      image: ASSETS.WHATSAPP_IMAGE_2026_05_01_AT_03_25_04_1 || ASSETS.BOATLIFTS_1 || '',
      overlay: 'bg-black/50'
    },

    title: 'Privacy Policy',
    effectiveDateLabel: 'Effective Date',
    effectiveDate: 'October 1, 2025',

    introParagraphs: [
      'Tropical Marine Construction ("we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website, request an estimate, contact us, or interact with our services — including by phone, email, form submission, or text message.',
    ],

    sections: [
      {
        id: 'collection',
        number: 1,
        heading: 'Information We Collect',
        paragraphs: [
          'We may collect the following types of information:',
          'Contact information: name, email address, phone number, property address, and company name when applicable.',
          'Project details: dock, boat lift, seawall, piling, repair, maintenance, or estimate information you submit.',
          'Communications: messages you send to us, including phone calls, emails, SMS messages, contact forms, and quote requests.',
          'Technical data: IP address, browser type, device information, website usage data, cookies, and analytics information.'
        ]
      },
      {
        id: 'data-usage',
        number: 2,
        heading: 'How We Use Your Information',
        paragraphs: [
          'We use your information to:',
          'Respond to estimate requests, service inquiries, and project questions.',
          'Schedule consultations, site visits, inspections, repairs, or project follow-ups.',
          'Communicate about project details, permits, scheduling, payments, and service updates.',
          'Send text messages you have consented to receive.',
          'Improve our website, marketing, customer service, and overall client experience.',
          'Comply with legal, licensing, insurance, permitting, and regulatory obligations.',
          'We do not sell your personal information to third parties.'
        ]
      },
      {
        id: 'sms',
        number: 3,
        heading: 'SMS Messaging',
        paragraphs: [
          'When you provide your mobile number and consent, you may receive text messages from Tropical Marine Construction. These messages may include estimate follow-ups, appointment reminders, project updates, scheduling details, or responses to prior conversations.',
          'Message frequency varies by interaction. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance. We retain opt-in and opt-out records as required by law.',
          'You opt in to receive SMS messages when you voluntarily provide your phone number through our website, contact form, estimate request, phone call, or other communication channel. These actions may constitute your express consent to receive transactional or service-related text messages from Tropical Marine Construction.'
        ]
      },
      {
        id: 'age',
        number: 4,
        heading: 'Age Restrictions',
        paragraphs: [
          'Our website and services are intended for individuals 18 years of age or older. We do not knowingly collect personal information from minors.'
        ]
      },
      {
        id: 'cookies',
        number: 5,
        heading: 'Cookies and Tracking',
        paragraphs: [
          'We may use cookies, pixels, analytics tools, or similar technologies to understand website traffic, improve user experience, and measure marketing performance. You can disable cookies in your browser settings at any time.'
        ]
      },
      {
        id: 'protection',
        number: 6,
        heading: 'Data Protection',
        paragraphs: [
          'We use reasonable administrative, technical, and organizational measures to protect your information from unauthorized access, disclosure, alteration, or misuse.'
        ]
      },
      {
        id: 'third-party',
        number: 7,
        heading: 'Third-Party Services',
        paragraphs: [
          'We may use trusted third-party services for website hosting, analytics, advertising, CRM, forms, scheduling, email, SMS, payment processing, or business operations.',
          'These providers may process information only as needed to help us operate our business and provide services to you.'
        ]
      },
      {
        id: 'rights',
        number: 8,
        heading: 'Your Rights',
        paragraphs: [
          'You may request to access, update, correct, or delete your personal information, withdraw communication consent, or ask questions about how your data is used.',
          'To exercise these rights, contact us using the contact information listed on this page.'
        ]
      },
      {
        id: 'changes',
        number: 9,
        heading: 'Changes to This Policy',
        paragraphs: [
          'We may update this Privacy Policy periodically. Updates will be posted on this page with a revised effective date.'
        ]
      }
    ],

    contactTitle: 'Contact Us',
    contactIntro: (info) => `If you have any questions about this Privacy Policy or our data practices, please contact us: ${info.businessName || 'Tropical Marine Construction'} | ${info.address || '1222 SE 47th St Suite C1, Cape Coral, FL 33904'} | ${info.email || 'info@tropicalmarineconstruction.com'} | ${info.phone || ''}`,

    conversionSection: {
      title: 'Your Privacy Matters to Us',
      subtitle: 'We operate with transparency and respect for your information. If you have questions about your data or communication preferences, our team is here to help.',
      bgImage: ASSETS.BOATLIFTS_1 || ASSETS.DOCKS_1 || '',
      bgColor: 'bg-gray-50',
      buttonText: 'Return Home'
    }
  },

  he: {
    seo: {
      title: 'Privacy Policy | Tropical Marine Construction',
      description: 'Learn how Tropical Marine Construction collects, uses, and protects your personal information.',
      keywords: 'Tropical Marine Construction privacy policy'
    },

    heroSection: {
      image: ASSETS.DOCKS_1 || ASSETS.BOATLIFTS_1 || '',
      overlay: 'bg-black/50'
    },

    title: 'Privacy Policy',
    effectiveDateLabel: 'Effective Date',
    effectiveDate: 'October 1, 2025',

    introParagraphs: [
      'Tropical Marine Construction ("we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website, request an estimate, contact us, or interact with our services.',
    ],

    sections: [
      {
        id: 'collection',
        number: 1,
        heading: 'Information We Collect',
        paragraphs: [
          'We may collect contact information, project details, communications, and technical website data.',
          'This may include your name, email address, phone number, property address, service request, and messages you send to us.'
        ]
      },
      {
        id: 'data-usage',
        number: 2,
        heading: 'How We Use Your Information',
        paragraphs: [
          'We use your information to respond to estimate requests, schedule consultations, manage project communication, provide service updates, improve our website, and comply with legal obligations.',
          'We do not sell your personal information to third parties.'
        ]
      },
      {
        id: 'sms',
        number: 3,
        heading: 'SMS Messaging',
        paragraphs: [
          'When you provide your mobile number and consent, you may receive text messages from Tropical Marine Construction related to estimates, scheduling, appointments, project updates, or prior conversations.',
          'Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance.'
        ]
      },
      {
        id: 'age',
        number: 4,
        heading: 'Age Restrictions',
        paragraphs: [
          'Our website and services are intended for individuals 18 years of age or older. We do not knowingly collect personal information from minors.'
        ]
      },
      {
        id: 'cookies',
        number: 5,
        heading: 'Cookies and Tracking',
        paragraphs: [
          'We may use cookies and analytics tools to improve our website and understand visitor behavior.'
        ]
      },
      {
        id: 'protection',
        number: 6,
        heading: 'Data Protection',
        paragraphs: [
          'We use reasonable measures to protect your information from unauthorized access, disclosure, or misuse.'
        ]
      },
      {
        id: 'rights',
        number: 7,
        heading: 'Your Rights',
        paragraphs: [
          'You may request to access, update, correct, or delete your personal information, or withdraw communication consent.',
          'To exercise these rights, contact us using the contact information listed on this page.'
        ]
      }
    ],

    contactTitle: 'Contact Us',
    contactIntro: (info) => `If you have questions about this Privacy Policy, please contact us: ${info.businessName || 'Tropical Marine Construction'} | ${info.address || '1222 SE 47th St Suite C1, Cape Coral, FL 33904'} | ${info.email || 'info@tropicalmarineconstruction.com'} | ${info.phone || ''}`,

    conversionSection: {
      title: 'Your Privacy Matters to Us',
      subtitle: 'We operate with transparency and respect for your information.',
      bgImage: ASSETS.BOATLIFTS_1 || ASSETS.DOCKS_1 || '',
      bgColor: 'bg-gray-50',
      buttonText: 'Return Home'
    }
  }
};

export function getPrivacyStrings(language) {
  return privacyData[language] || privacyData.en;
}

export default privacyData;